import React from 'react';
import { formatDate, getEndTime } from '../../lib/utils';

/**
 * Unified date+time renderer used across customer & admin booking views.
 *
 * Layout: date and time on the same line separated by a vertical divider,
 * wrapping gracefully on narrow screens.
 *
 *   Wed, May 6, 2026  │  23:00 - 00:00
 *
 * Sizing tokens:
 *   - date: text-xl sm:text-2xl font-semibold
 *   - time: text-base sm:text-lg font-medium
 *
 * Props:
 *   - date:        ISO-style booking date string (required)
 *   - timeSlot:    "HH:MM-HH:MM" start slot (required)
 *   - duration:    number of hours; defaults to 1. When > 1 the end time is
 *                  computed via getEndTime() so multi-hour bookings show
 *                  the correct overall span.
 *   - className:   optional wrapper override
 *   - testId:      optional data-testid suffix for tests
 */
export const DateTimeDisplay = ({
  date,
  timeSlot,
  duration = 1,
  className = '',
  testId,
}) => {
  if (!date || !timeSlot) return null;

  const start = String(timeSlot).split('-')[0];
  const computedEnd = getEndTime(timeSlot, duration);
  const displayTime =
    Number(duration) > 1 ? `${start} - ${computedEnd}` : timeSlot;

  return (
    <div
      className={`flex flex-wrap items-center gap-y-1 ${className}`}
      data-testid={testId ? `datetime-${testId}` : undefined}
    >
      <span className="text-xl sm:text-2xl font-semibold leading-tight">
        {formatDate(date)}
      </span>
      <span
        aria-hidden="true"
        className="hidden sm:inline-block h-6 w-px bg-border mx-3"
      />
      <span className="text-base sm:text-lg font-medium leading-tight w-full sm:w-auto">
        {displayTime}
      </span>
    </div>
  );
};

export default DateTimeDisplay;
